import React, { type ReactElement, type ReactNode } from 'react';
import { type DataThemeOptions, FooterActionBar } from '@saviynt/design-system';
import cx from 'classnames';

import { testIds } from '../../../tests';

import './SodFooter.css';

/**
 * Interface for SodFooter component props
 */
interface SodFooterProps {
  children?: ReactNode | string;
  className?: string;
  dataTheme?: DataThemeOptions;
}

/**
 * SodFooter Component
 *
 * TODO:
 * - isSideNavExpanded
 *
 * A reusable footer component that wraps the FooterActionBar from the design system.
 * It provides a consistent footer layout with theme support and customizable content.
 *
 * @component
 * @param {SodFooterProps} props - The props for the component
 * @param {ReactNode | string} [props.children] - Content to be rendered inside the footer
 * @param {string} [props.className] - Additional CSS class names
 * @param {DataThemeOptions} [props.dataTheme='dark'] - Theme option for the footer
 *
 * @returns {ReactElement} A footer component with the specified content and styling
 *
 * @example
 * // Basic usage
 * <SodFooter>
 *   <button>Save</button>
 * </SodFooter>
 *
 * // With custom theme and className
 * <SodFooter dataTheme="light" className="custom-footer">
 *   <button>Cancel</button>
 *   <button>Submit</button>
 * </SodFooter>
 */
function SodFooter({
  children,
  className,
  dataTheme,
}: SodFooterProps): ReactElement {
  const footerClasses = cx(
    'SodFooter',
    // isSideNavExpanded && 'SodFooter--isSideNavExpanded',
    className
  );

  return (
    <FooterActionBar
      className={footerClasses}
      dataTestId={testIds.SOD_VIOLATIONS_PAGE_FOOTER}
      dataTheme={dataTheme}>
      {children}
    </FooterActionBar>
  );
}

/**
 * Default props for the SodFooter component
 */
SodFooter.defaultProps = {
  children: <div />,
  className: null,
  dataTheme: 'dark',
};

export default React.memo(SodFooter);
